'use client'

import Link from 'next/link'
import React, { useMemo } from 'react'
import { EllipsisVerticalIcon, SearchIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { CMSLink } from '@/components/Link'
import { Button } from '@/components/ui/button'
import { useHeaderNavigationContext } from '@/providers/HeaderNavigation'
import { cn } from '@/utilities/ui'

import type { Header as HeaderType } from '@/payload-types'

export const NavCenter: React.FC<{
  data: HeaderType['centerNav']
}> = ({ data }) => {
  const { colorInverse, isDropdownOpen, setIsDropdownOpen, dropdownRef, setActiveCallout } =
    useHeaderNavigationContext()
  const main = data?.main

  const LINK_CLASSES = useMemo(() => {
    return cn(
      'text-reg! font-medium text-neutral-900 opacity-100 group-hover/nav-center:text-neutral-500 hover:bg-accent-foreground/50 hover:text-neutral-900 hover:opacity-100',
      colorInverse &&
        'text-white group-hover/nav-center:text-neutral-300 hover:bg-accent/25 hover:text-white focus-visible:ring-evergreen-300!',
    )
  }, [colorInverse])

  if (!main) return null

  return (
    <div className="nav-center hidden h-full items-center gap-0 py-2.5 lg:flex">
      <motion.div
        id="nav-center"
        onHoverStart={() => setIsDropdownOpen(true)}
        onHoverEnd={() => setIsDropdownOpen(false)}
        className="group/nav-center flex h-full cursor-pointer gap-1"
        aria-controls="nav-dropdown"
        aria-label="Main navigation center"
        aria-haspopup="menu"
        aria-expanded={isDropdownOpen}
      >
        {main.map((item, index) => (
          <Button
            key={item.id}
            variant="link"
            icon={false}
            asChild
            className={cn(LINK_CLASSES, 'px-3.5')}
            onMouseEnter={() => setActiveCallout(index)}
          >
            {item.link.type === 'reference' ? (
              <CMSLink {...item.link} />
            ) : (
              <Link
                href={item.link.url || '/'}
                target={item.link.newTab ? '_blank' : '_self'}
                rel={item.link.newTab ? 'noopener noreferrer' : undefined}
              >
                {item.link.label}
              </Link>
            )}
          </Button>
        ))}
      </motion.div>

      {/* Separator */}
      {/* <div
        aria-hidden="true"
        className={cn(
          'w-px h-[calc(50%-0.5rem)] bg-border/50 -mx-2 self-center',
          colorInverse && 'bg-border-inverse/50',
        )}
      /> */}

      {/* Menu button */}
      <Button
        variant="link"
        icon={false}
        className={cn(LINK_CLASSES, 'relative size-10 items-center justify-center p-0')}
        aria-controls="nav-dropdown"
        aria-label="Open menu"
        aria-haspopup="menu"
        aria-expanded={isDropdownOpen}
        onClick={() => {
          setActiveCallout(null)
          setIsDropdownOpen(!isDropdownOpen)
          if (isDropdownOpen) {
            dropdownRef.current?.setAttribute('visibility', 'visible')
            const firstFocusableElement = dropdownRef.current?.querySelector(
              'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
            ) as HTMLElement
            if (firstFocusableElement) {
              setTimeout(() => {
                firstFocusableElement.focus()
              }, 100)
            }
          }
        }}
      >
        <span className="sr-only">Menu</span>
        <AnimatePresence mode="wait">
          <motion.span
            key={isDropdownOpen ? 'close' : 'open'}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
          >
            {isDropdownOpen ? (
              <XIcon className="size-5" />
            ) : (
              <EllipsisVerticalIcon className="size-5" />
            )}
          </motion.span>
        </AnimatePresence>
      </Button>

      <Button
        variant="link"
        icon={false}
        asChild
        className={cn(LINK_CLASSES, 'size-10! items-center justify-center p-0')}
      >
        <Link href="/search">
          <span className="sr-only">Search</span>
          <SearchIcon className="size-5" />
        </Link>
      </Button>
    </div>
  )
}
